/**
 * Happy Path Wizard - AUTOMATED Guided Mode for First Item Generation
 * 
 * This wizard EXECUTES each step automatically when the user clicks "Next".
 * No manual navigation required - each step runs the full sub-workflow.
 * 
 * Steps:
 * 1. Sources - Select source with chunks (shows ALL sources with chunks)
 * 2. Proposals - Auto: Extract → Submit → Approve → Merge
 * 3. AKUs - Auto: Extract Claims → Promote to AKU v2
 * 4. Racks - Auto: Build Rack from AKUs
 * 5. Generate - Auto: Generate Items from Rack
 * 6. Validate - Auto: Run 5-stage validation pipeline on generated items
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  CheckCircle,
  Circle,
  AlertCircle,
  Database,
  Network,
  Layers,
  FileText,
  Sparkles,
  RefreshCw,
  Play,
  Loader2,
  ArrowDown,
  Rocket,
  Zap,
  Shield,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
// All API calls must use relative URLs (/api/...) or window.location.origin
// NEVER use an external domain like emergent.host
const API = ''; // Relative URL - browser uses same origin

// P0 (C5): Helper to detect and report fetch/CORS errors clearly
const isFetchError = (error) => {
  const msg = error?.message?.toLowerCase() || '';
  return msg.includes('failed to fetch') || 
         msg.includes('network') || 
         msg.includes('cors') ||
         msg.includes('blocked');
};

const getFetchErrorHint = () => {
  return 'CORS/Routing error detected. Check: ' +
         '(1) API base URL must be relative (/api/...), ' +
         '(2) No cross-origin calls to external domains.';
};

const StepStatus = {
  COMPLETE: 'complete',
  READY: 'ready',
  BLOCKED: 'blocked',
  IN_PROGRESS: 'in_progress'
};

const STATUS_CONFIG = {
  [StepStatus.COMPLETE]: {
    color: 'bg-green-100 text-green-800 border-green-300',
    badgeText: 'Complete',
    icon: CheckCircle,
    iconColor: 'text-green-600'
  },
  [StepStatus.READY]: {
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    badgeText: 'Ready',
    icon: Circle,
    iconColor: 'text-blue-600'
  },
  [StepStatus.BLOCKED]: {
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    badgeText: 'Blocked',
    icon: AlertCircle,
    iconColor: 'text-yellow-600'
  },
  [StepStatus.IN_PROGRESS]: {
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    badgeText: 'Running...',
    icon: Loader2,
    iconColor: 'text-purple-600 animate-spin'
  }
};

const HappyPathWizard = ({ 
  isActive = false,
  onNavigateToTab = null,
  onNavigateToEvidence = null,
  onNavigateToChunks = null,
  examProfileId = 'PSM1'
}) => {
  const { getAuthHeaders } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedExam, setSelectedExam] = useState(examProfileId);
  const [examProfiles, setExamProfiles] = useState([]);
  
  // Course selection (P0.2: Course → exam_profile preselect)
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  
  // Source selection
  const [sources, setSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const sourcesLoadingRef = useRef(false); // P0 FIX: Use ref to prevent concurrent fetches
  
  // Sync selectedExam when examProfileId prop changes
  useEffect(() => {
    if (examProfileId && examProfileId !== selectedExam) {
      setSelectedExam(examProfileId);
    }
  }, [examProfileId]);
  
  // Step states - Now 6 steps including validation
  const [currentStep, setCurrentStep] = useState(1);
  const [stepStatus, setStepStatus] = useState({
    1: { status: StepStatus.BLOCKED, hint: 'Loading...', data: null },
    2: { status: StepStatus.BLOCKED, hint: 'Waiting for Step 1', data: null },
    3: { status: StepStatus.BLOCKED, hint: 'Waiting for Step 2', data: null },
    4: { status: StepStatus.BLOCKED, hint: 'Waiting for Step 3', data: null },
    5: { status: StepStatus.BLOCKED, hint: 'Waiting for Step 4', data: null },
    6: { status: StepStatus.BLOCKED, hint: 'Waiting for Step 5', data: null }
  });
  
  // Track generated items for validation
  const [generatedItemIds, setGeneratedItemIds] = useState([]);
  
  // P1.8: Systemweiter run_key für Tracing
  const [currentRunKey, setCurrentRunKey] = useState(null);
  
  // Run-Key Policy: Store last run_key for "Resume" capability
  const [lastRunKey, setLastRunKey] = useState(null);
  const [runKeyHistory, setRunKeyHistory] = useState([]); // Last 20 run_keys
  const [showRunKeyHistory, setShowRunKeyHistory] = useState(false);
  
  // P2: Gate Status tracking
  const [gateStatus, setGateStatus] = useState({
    source: null,  // S1, S2 gates
    akus: null,    // V1, V2, V3 gates
    rack: null     // V4 gate
  });
  
  // Execution state
  const [executing, setExecuting] = useState(false);
  const [executionLog, setExecutionLog] = useState([]);

  // P0: Robust API call helper - handles HTML responses, auth errors, etc.
  const robustApiCall = async (url, options, logFn) => {
    const shortUrl = url.replace(API, '');
    logFn(`   📡 ${options.method || 'GET'} ${shortUrl}`, 'info');
    
    try {
      const response = await fetch(url, options);
      const contentType = response.headers.get('content-type') || '';
      
      logFn(`   📊 HTTP ${response.status}, Content-Type: ${contentType.split(';')[0]}`, 
        response.ok ? 'info' : 'warning');
      
      // Handle HTML responses (auth redirect, proxy error, 404 page, etc.)
      if (contentType.includes('text/html')) {
        const htmlSnippet = (await response.text()).substring(0, 150);
        return {
          ok: false,
          isHtml: true,
          status: response.status,
          error: 'API returned HTML instead of JSON',
          htmlSnippet: htmlSnippet.replace(/\n/g, ' ').substring(0, 80),
          adminHint: 'Prüfen Sie Auth-Token, Base-URL, und Proxy-Konfiguration'
        };
      }
      
      // Parse JSON response
      if (response.ok) {
        const data = await response.json();
        return { ok: true, data, status: response.status };
      } else {
        try {
          const errorData = await response.json();
          return { 
            ok: false, 
            status: response.status, 
            error: errorData.detail || errorData.message || JSON.stringify(errorData).substring(0, 100),
            data: errorData
          };
        } catch {
          const text = await response.text();
          return { 
            ok: false, 
            status: response.status, 
            error: text.substring(0, 100)
          };
        }
      }
    } catch (networkError) {
      return {
        ok: false,
        isNetworkError: true,
        error: networkError.message,
        adminHint: 'Prüfen Sie Netzwerkverbindung und API-Verfügbarkeit'
      };
    }
  };

  // Add log entry
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('de-DE');
    setExecutionLog(prev => [...prev, { timestamp, message, type }]);
  };

  // P2: Check Source Gates (S1, S2)
  const checkSourceGates = useCallback(async (sourceId) => {
    try {
      const response = await fetch(
        `${API}/api/gates/source/${sourceId}/status?exam_profile_id=${selectedExam}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setGateStatus(prev => ({ ...prev, source: data }));
        return data;
      }
    } catch (error) {
      console.error('Error checking source gates:', error);
    }
    return null;
  }, [getAuthHeaders, selectedExam]);

  // P2: Check AKU Gates (V1, V2, V3)
  const checkAkuGates = useCallback(async (akuId) => {
    try {
      const response = await fetch(
        `${API}/api/gates/aku/${akuId}/status?exam_profile_id=${selectedExam}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setGateStatus(prev => ({ ...prev, akus: data }));
        return data;
      }
    } catch (error) {
      console.error('Error checking AKU gates:', error);
    }
    return null;
  }, [getAuthHeaders, selectedExam]);

  // P2: Check Rack Gates (V4)
  const checkRackGates = useCallback(async (rackId) => {
    try {
      const response = await fetch(
        `${API}/api/gates/rack/${rackId}/status?exam_profile_id=${selectedExam}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setGateStatus(prev => ({ ...prev, rack: data }));
        return data;
      }
    } catch (error) {
      console.error('Error checking Rack gates:', error);
    }
    return null;
  }, [getAuthHeaders, selectedExam]);

  // Fetch exam profiles
  const fetchExamProfiles = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/exam-configs`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not { exams: [...] }
        setExamProfiles(Array.isArray(data) ? data : data.exams || []);
      }
    } catch (error) {
      console.error('Error fetching exam profiles:', error);
    }
  }, [getAuthHeaders]);

  // Fetch courses for preselection (P0.2)
  const fetchCourses = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/admin/course-migration/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const courseList = data?.status?.courses?.list || [];
        setCourses(courseList);
        
        // Auto-select first course if none selected
        if (!selectedCourse && courseList.length > 0) {
          const firstCourse = courseList[0];
          setSelectedCourse(firstCourse.code);
          if (firstCourse.default_exam_profile_id) {
            setSelectedExam(firstCourse.default_exam_profile_id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, [getAuthHeaders, selectedCourse]);

  // Handle course change - auto-select exam_profile
  const handleCourseChange = useCallback((courseCode) => {
    setSelectedCourse(courseCode);
    const course = courses.find(c => c.code === courseCode);
    if (course?.default_exam_profile_id) {
      setSelectedExam(course.default_exam_profile_id);
    }
  }, [courses]);

  // Fetch sources with chunks - P0 FIX: Filter by selected exam to prevent cross-exam content
  const fetchSources = useCallback(async () => {
    // P0 FIX: Prevent concurrent fetches using ref
    if (sourcesLoadingRef.current) {
      return;
    }
    sourcesLoadingRef.current = true;
    
    try {
      // P0 FIX: Add exam_id filter to only show sources that match the selected exam
      const examFilter = selectedExam ? `&exam_id=${selectedExam}` : '';
      
      const headers = getAuthHeaders();
      
      const response = await fetch(`${API}/api/source-registry/list?limit=50${examFilter}`, {
        headers
      });
      
      
      if (response.ok) {
        const data = await response.json();
        const items = data.items || [];
        
        // Fetch health for each source to get chunk counts
        const sourcesWithHealth = await Promise.all(
          items.map(async (source) => {
            try {
              const healthRes = await fetch(
                `${API}/api/source-registry/${source.source_id}/health`,
                { headers: getAuthHeaders() }
              );
              if (healthRes.ok) {
                const health = await healthRes.json();
                return { ...source, chunk_count: health.chunk_count || 0, health };
              }
            } catch (e) {
              console.error(`Health check failed for ${source.source_id}:`, e);
            }
            return { ...source, chunk_count: 0 };
          })
        );
        
        // Filter to sources with chunks
        const withChunks = sourcesWithHealth.filter(s => s.chunk_count > 0);
        setSources(withChunks);
        
        // P0 FIX: Update step status based on sources found
        if (withChunks.length === 0) {
          setStepStatus(prev => ({
            ...prev,
            1: { status: StepStatus.BLOCKED, hint: `Keine Sources für ${selectedExam} gefunden`, data: null }
          }));
        }
        
        // Auto-select first source with chunks that matches the exam
        if (withChunks.length > 0 && !selectedSourceId) {
          setSelectedSourceId(withChunks[0].source_id);
          // P0 FIX: Also update step status to READY
          setStepStatus(prev => ({
            ...prev,
            1: { status: StepStatus.READY, hint: `${withChunks.length} Sources verfügbar`, data: { source_count: withChunks.length } }
          }));
        }
        
        // P0 FIX: Clear selection if current source doesn't match exam
        if (selectedSourceId && withChunks.length > 0) {
          const currentSourceMatchesExam = withChunks.some(s => s.source_id === selectedSourceId);
          if (!currentSourceMatchesExam) {
            setSelectedSourceId(withChunks[0].source_id);
            toast.warning('Source changed - previous source does not match selected exam');
          }
        }
      } else {
        console.error('[HappyPath] Sources fetch failed:', response.status, response.statusText);
        // Try to parse error message
        try {
          const errorData = await response.json();
          console.error('[HappyPath] Error details:', errorData);
        } catch (e) {}
      }
    } catch (error) {
      console.error('[HappyPath] Error fetching sources:', error);
      if (isFetchError(error)) {
        toast.error(getFetchErrorHint());
      }
    } finally {
      sourcesLoadingRef.current = false; // P0 FIX: Always clear loading state
    }
  }, [getAuthHeaders, selectedSourceId, selectedExam]);

  // Refresh all step statuses
  const refreshStatuses = useCallback(async () => {
    setRefreshing(true);
    try {
      const headers = getAuthHeaders();
      
      // Step 1: Check sources - fetch fresh data with exam filter
      // P0 FIX: Add exam_id filter to only show sources that match the selected exam
      const examFilter = selectedExam ? `&exam_id=${selectedExam}` : '';
      const sourcesRes = await fetch(`${API}/api/source-registry/list?limit=50${examFilter}`, { headers });
      let sourcesWithHealth = [];
      if (sourcesRes.ok) {
        const sourcesData = await sourcesRes.json();
        const items = sourcesData.items || [];
        
        // Fetch health for each source
        sourcesWithHealth = await Promise.all(
          items.map(async (source) => {
            try {
              const healthRes = await fetch(
                `${API}/api/source-registry/${source.source_id}/health`,
                { headers }
              );
              if (healthRes.ok) {
                const health = await healthRes.json();
                return { ...source, chunk_count: health.chunk_count || 0 };
              }
            } catch (e) {}
            return { ...source, chunk_count: 0 };
          })
        );
        
        const withChunks = sourcesWithHealth.filter(s => s.chunk_count > 0);
        setSources(withChunks);
        
        // Auto-select first source if none selected
        if (withChunks.length > 0 && !selectedSourceId) {
          setSelectedSourceId(withChunks[0].source_id);
        }
        
        // P0 FIX: Clear selection if current source doesn't match exam
        if (selectedSourceId && withChunks.length > 0) {
          const currentSourceMatchesExam = withChunks.some(s => s.source_id === selectedSourceId);
          if (!currentSourceMatchesExam) {
            setSelectedSourceId(withChunks[0].source_id);
          }
        }
      }
      
      const selectedSource = sourcesWithHealth.find(s => s.source_id === selectedSourceId);
      const hasChunks = selectedSource?.chunk_count > 0;
      
      setStepStatus(prev => ({
        ...prev,
        1: {
          status: hasChunks ? StepStatus.COMPLETE : StepStatus.READY,
          hint: hasChunks 
            ? `${selectedSource.title || selectedSource.canonical_name || selectedSource.source_id}: ${selectedSource.chunk_count} Chunks` 
            : `${sourcesWithHealth.filter(s => s.chunk_count > 0).length} Sources mit Chunks verfügbar`,
          data: selectedSource
        }
      }));

      if (!selectedSourceId) {
        setRefreshing(false);
        return;
      }

      // Step 2: Check proposals for this source
      const proposalsRes = await fetch(
        `${API}/api/ontology-proposals?source_id=${selectedSourceId}&limit=100`,
        { headers }
      );
      const proposalsData = proposalsRes.ok ? await proposalsRes.json() : { items: [] };
      const proposals = proposalsData.items || [];
      
      const byStatus = {};
      proposals.forEach(p => {
        const st = p.status || 'UNKNOWN';
        byStatus[st] = (byStatus[st] || 0) + 1;
      });
      
      const mergedProposals = byStatus.MERGED || 0;
      const approvedProposals = byStatus.APPROVED || 0;
      const draftProposals = byStatus.DRAFT || 0;
      const totalProposals = proposals.length;
      const hasProposals = totalProposals > 0;
      
      // Step 2 status logic:
      // COMPLETE: Has merged proposals (fully processed)
      // READY: Has proposals to process OR has approved proposals waiting to be merged
      // BLOCKED: No proposals and no source selected
      const step2Status = mergedProposals > 0 ? StepStatus.COMPLETE :
                          (approvedProposals > 0) ? StepStatus.READY :
                          hasProposals ? StepStatus.READY : 
                          StepStatus.READY; // Ready for extraction
      
      const step2Hint = mergedProposals > 0 
        ? `✓ ${mergedProposals} Proposals gemerged`
        : approvedProposals > 0 
          ? `${approvedProposals} Proposals APPROVED (bereit zum Mergen)`
          : hasProposals 
            ? `${totalProposals} Proposals (${Object.entries(byStatus).map(([k,v]) => `${k}: ${v}`).join(', ')})`
            : 'Bereit für Proposal-Extraktion';
      
      setStepStatus(prev => ({
        ...prev,
        2: {
          status: step2Status,
          hint: step2Hint,
          data: { proposals, byStatus, mergedProposals, approvedProposals, totalProposals }
        }
      }));

      // Step 3: Check claims/AKUs (don't filter by source_id for AKUs - they may not have it)
      const claimsRes = await fetch(
        `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&limit=100`,
        { headers }
      );
      const claimsData = claimsRes.ok ? await claimsRes.json() : { items: [] };
      const claims = claimsData.items || [];
      
      const claimsByStatus = {};
      claims.forEach(c => {
        const st = c.status || 'UNKNOWN';
        claimsByStatus[st] = (claimsByStatus[st] || 0) + 1;
      });
      
      // Get ALL AKUs for this exam profile (not filtered by source)
      const akusRes = await fetch(
        `${API}/api/akus-v2?exam_profile_id=${selectedExam}&limit=100`,
        { headers }
      );
      const akusData = akusRes.ok ? await akusRes.json() : { items: [] };
      const akus = akusData.items || [];
      
      const mergedClaims = claimsByStatus.MERGED || 0;
      const draftClaims = claimsByStatus.DRAFT || 0;
      const totalClaims = claims.length;
      
      // Step 3 is READY if:
      // - We have ANY proposals (from Step 2) to work with
      // - OR we have claims that need processing
      // Step 3 is COMPLETE if we have AKUs
      const step3Ready = totalProposals > 0 || totalClaims > 0 || akus.length > 0;
      
      setStepStatus(prev => ({
        ...prev,
        3: {
          status: akus.length > 0 ? StepStatus.COMPLETE :
                  step3Ready ? StepStatus.READY : StepStatus.BLOCKED,
          hint: akus.length > 0 
            ? `${akus.length} AKUs erstellt`
            : mergedClaims > 0
              ? `${mergedClaims} Claims bereit für Promotion`
              : totalClaims > 0
                ? `${totalClaims} Claims vorhanden (${Object.entries(claimsByStatus).map(([k,v]) => `${k}: ${v}`).join(', ')})`
                : totalProposals > 0
                  ? 'Bereit für Claim-Extraktion'
                  : 'Warte auf Step 2',
          data: { claims, claimsByStatus, akus, mergedClaims, totalProposals }
        }
      }));

      // Step 4: Check racks
      const racksRes = await fetch(
        `${API}/api/racks?exam_profile_id=${selectedExam}&limit=50`,
        { headers }
      );
      const racksData = racksRes.ok ? await racksRes.json() : { items: [] };
      const racks = racksData.items || [];
      
      // Filter racks that have source_akus from our AKUs
      const relevantRacks = racks.filter(r => {
        const rackAkus = r.source_akus || [];
        return rackAkus.some(akuId => akus.some(a => a.aku_id === akuId));
      });
      
      setStepStatus(prev => ({
        ...prev,
        4: {
          status: relevantRacks.length > 0 ? StepStatus.COMPLETE :
                  akus.length > 0 ? StepStatus.READY : StepStatus.BLOCKED,
          hint: relevantRacks.length > 0 
            ? `${relevantRacks.length} Racks erstellt`
            : akus.length > 0
              ? 'Bereit für Rack-Erstellung'
              : 'Erst AKUs erstellen',
          data: { racks: relevantRacks, allRacks: racks }
        }
      }));

      // Step 5: Check generated items
      const genRes = await fetch(
        `${API}/api/generate/stats?exam_profile_id=${selectedExam}`,
        { headers }
      );
      const genData = genRes.ok ? await genRes.json() : {};
      const totalItems = genData.total_items_generated || 0;
      
      // Get generated items for validation status check
      const itemsRes = await fetch(
        `${API}/api/generate/items?exam_profile_id=${selectedExam}&limit=50`,
        { headers }
      );
      const itemsData = itemsRes.ok ? await itemsRes.json() : { items: [] };
      const generatedItems = itemsData.items || [];
      
      // Count validated items (those with validation_result)
      const validatedItems = generatedItems.filter(i => i.validation_result || i.validation_status === 'completed');
      const approvedItems = generatedItems.filter(i => i.status === 'APPROVED');
      const draftItems = generatedItems.filter(i => i.status === 'DRAFT');
      
      setStepStatus(prev => ({
        ...prev,
        5: {
          status: totalItems > 0 ? StepStatus.COMPLETE :
                  (relevantRacks.length > 0 || racks.length > 0) ? StepStatus.READY : StepStatus.BLOCKED,
          hint: totalItems > 0 
            ? `${totalItems} Items generiert`
            : racks.length > 0
              ? 'Bereit für Item-Generierung'
              : 'Erst Rack erstellen',
          data: { ...genData, generatedItems }
        }
      }));
      
      // Step 6: Check validation status
      const allValidated = draftItems.length === 0 && validatedItems.length > 0;
      const someValidated = validatedItems.length > 0;
      
      setStepStatus(prev => ({
        ...prev,
        6: {
          status: allValidated ? StepStatus.COMPLETE :
                  totalItems > 0 ? StepStatus.READY : StepStatus.BLOCKED,
          hint: allValidated 
            ? `${approvedItems.length} genehmigt, ${validatedItems.length} validiert`
            : someValidated
              ? `${validatedItems.length}/${totalItems} validiert (${draftItems.length} ausstehend)`
              : totalItems > 0
                ? `${totalItems} Items bereit für Validierung`
                : 'Erst Items generieren',
          data: { 
            totalItems, 
            validatedCount: validatedItems.length, 
            approvedCount: approvedItems.length,
            draftCount: draftItems.length,
            generatedItems
          }
        }
      }));

    } catch (error) {
      console.error('Error refreshing statuses:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setRefreshing(false);
    }
  }, [getAuthHeaders, fetchSources, sources, selectedSourceId, selectedExam]);

  // ==================== AUTOMATED STEP EXECUTION ====================

  // Execute Step 2: Extract Proposals → Submit → Approve → Merge
  const executeStep2 = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let extractedInThisRun = 0; // B5: Track extraction count for consistency logging
    
    addLog('🚀 Step 2: Starte Proposal-Workflow...', 'info');
    
    // 2a: Extract Proposals from Source (need ontology_version_id)
    addLog('📥 Extrahiere Ontology-Proposals aus Source...', 'info');
    try {
      // First get current ontology version - P0 FIX: Filter by exam_id
      const versionsRes = await fetch(`${API}/api/ontology-versions?exam_id=${selectedExam}`, { headers });
      let ontologyVersionId = null;
      
      if (versionsRes.ok) {
        const versions = await versionsRes.json();
        addLog(`   📊 ${versions.length} Ontology-Versionen für ${selectedExam} gefunden`, 'info');
        // P0 FIX: Prefer released version for this exam, then any version, then first available
        const releasedForExam = versions.find(v => v.is_released && v.exam_ids?.includes(selectedExam));
        const anyForExam = versions.find(v => v.exam_ids?.includes(selectedExam));
        const released = versions.find(v => v.is_released);
        ontologyVersionId = releasedForExam?.version_id || anyForExam?.version_id || released?.version_id || (versions[0]?.version_id);
        if (ontologyVersionId) {
          addLog(`   ✓ Verwende Version: ${ontologyVersionId}`, 'info');
        }
      }
      
      // Wenn keine Version existiert, erstelle eine Staging-Baseline
      if (!ontologyVersionId) {
        addLog('📝 Erstelle Staging-Ontology-Version...', 'info');
        const createRes = await fetch(`${API}/api/ontology-versions/create-snapshot`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            exam_ids: [selectedExam],
            bump_type: 'MINOR',
            release_notes: 'Staging baseline für Workflow',
            auto_release: false  // Nur Staging, nicht releasen
          })
        });
        
        if (createRes.ok) {
          const newVersion = await createRes.json();
          ontologyVersionId = newVersion.version_id;
          addLog(`✅ Staging-Version erstellt: ${newVersion.version_number || ontologyVersionId}`, 'success');
        } else {
          const errText = await createRes.text();
          addLog(`⚠️ Ontology-Version konnte nicht erstellt werden: ${errText.substring(0, 80)}`, 'warning');
        }
      }
      
      if (ontologyVersionId) {
        const extractRes = await fetch(
          `${API}/api/ontology/extract-proposals`,
          { 
            method: 'POST', 
            headers,
            body: JSON.stringify({
              source_id: selectedSourceId,
              exam_profile_id: selectedExam,
              ontology_version_id: ontologyVersionId,
              include_relationships: true,
              run_key: runKey
            })
          }
        );
        
        if (extractRes.ok) {
          const result = await extractRes.json();
          extractedInThisRun = result.proposals_created || result.created_count || 0;
          const dedupeHits = result.dedupe_hits || 0;
          const skipped = result.skipped_count || 0;
          
          // IDEMPOTENT logging: show created vs skipped
          if (result.status === 'IDEMPOTENT_SKIP') {
            addLog(`ℹ️ IDEMPOTENT: Run bereits ausgeführt (run_key: ${runKey})`, 'info');
          } else if (dedupeHits > 0) {
            addLog(`✅ ${extractedInThisRun} erstellt, ${dedupeHits} Duplikate übersprungen`, 'success');
          } else {
            addLog(`✅ ${extractedInThisRun} Proposals extrahiert`, 'success');
          }
        } else {
          const errText = await extractRes.text();
          addLog(`⚠️ Proposal-Extraktion: ${errText.substring(0, 100)}`, 'warning');
        }
      } else {
        addLog('⚠️ Keine Ontology-Version verfügbar - überspringe CONCEPT-Extraktion', 'warning');
        addLog('ℹ️ Claims werden trotzdem extrahiert (Step 3)', 'info');
      }
    } catch (e) {
      // P0 (C5): HARD STOP on fetch/CORS errors
      if (isFetchError(e)) {
        addLog(`❌ FETCH FAILED: ${e.message}`, 'error');
        addLog(`   💡 ${getFetchErrorHint()}`, 'error');
        addLog('❌ WORKFLOW HARD STOP - Bitte API-Konfiguration prüfen', 'error');
        return { fetchFailed: true, reason: 'CORS_OR_NETWORK_ERROR' };
      }
      addLog(`⚠️ Proposal-Extraktion: ${e.message}`, 'warning');
    }
    
    // 2b: Get all DRAFT proposals and submit for review
    // IDEMPOTENT: Only processes proposals from this run_key (strict scoping)
    addLog('📤 Reiche Proposals zur Review ein...', 'info');
    try {
      // Query for DRAFT proposals with run_key scope
      const draftRes = await fetch(
        `${API}/api/ontology-proposals?source_id=${selectedSourceId}&status=DRAFT&run_key=${runKey}&limit=200`,
        { headers }
      );
      const draftData = await draftRes.json();
      const draftIds = (draftData.items || []).map(p => p.proposal_id);
      
      if (draftIds.length > 0) {
        const submitRes = await fetch(`${API}/api/ontology-proposals/bulk/submit-for-review`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ proposal_ids: draftIds, run_key: runKey })
        });
        if (submitRes.ok) {
          const result = await submitRes.json();
          const submittedCount = result.modified_count || draftIds.length;
          addLog(`✅ ${submittedCount} Proposals submitted`, 'success');
        }
      } else {
        // Check if there are unscoped DRAFT proposals (from previous runs)
        const unscopedRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&status=DRAFT&limit=1`,
          { headers }
        );
        const unscopedData = await unscopedRes.json();
        if ((unscopedData.total_count || 0) > 0) {
          addLog(`ℹ️ ${unscopedData.total_count} DRAFT Proposals aus vorherigen Runs (werden ignoriert)`, 'info');
        } else {
          addLog('ℹ️ Keine DRAFT Proposals zum Submitten', 'info');
        }
      }
    } catch (e) {
      // P0 (C5): HARD STOP on fetch/CORS errors
      if (isFetchError(e)) {
        addLog(`❌ FETCH FAILED (Submit): ${e.message}`, 'error');
        addLog(`   💡 ${getFetchErrorHint()}`, 'error');
        return { fetchFailed: true, reason: 'CORS_OR_NETWORK_ERROR' };
      }
      addLog(`⚠️ Submit: ${e.message}`, 'warning');
    }
    
    // 2c: Approve all REVIEW proposals (run_key scoped)
    addLog('👍 Approve alle Proposals...', 'info');
    try {
      const reviewRes = await fetch(
        `${API}/api/ontology-proposals?source_id=${selectedSourceId}&status=REVIEW&run_key=${runKey}&limit=200`,
        { headers }
      );
      const reviewData = await reviewRes.json();
      const reviewIds = (reviewData.items || []).map(p => p.proposal_id);
      
      if (reviewIds.length > 0) {
        const approveRes = await fetch(`${API}/api/ontology-proposals/bulk/approve`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ proposal_ids: reviewIds, run_key: runKey })
        });
        if (approveRes.ok) {
          const result = await approveRes.json();
          addLog(`✅ ${result.approved_count || reviewIds.length} Proposals approved`, 'success');
        }
      } else {
        addLog('ℹ️ Keine REVIEW Proposals zum Approven', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Approve: ${e.message}`, 'warning');
    }
    
    // 2d: Merge all APPROVED proposals (run_key scoped)
    addLog('🔀 Merge alle approved Proposals...', 'info');
    try {
      const approvedRes = await fetch(
        `${API}/api/ontology-proposals?source_id=${selectedSourceId}&status=APPROVED&run_key=${runKey}&limit=200`,
        { headers }
      );
      const approvedData = await approvedRes.json();
      const approvedIds = (approvedData.items || []).map(p => p.proposal_id);
      
      if (approvedIds.length > 0) {
        const mergeRes = await fetch(`${API}/api/ontology-proposals/bulk/merge`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ proposal_ids: approvedIds, run_key: runKey })
        });
        if (mergeRes.ok) {
          const result = await mergeRes.json();
          addLog(`✅ ${result.merged_count || approvedIds.length} Proposals gemerged`, 'success');
        }
      } else {
        addLog('ℹ️ Keine APPROVED Proposals zum Mergen', 'info');
      }
    } catch (e) {
      // P0 (C5): HARD STOP on fetch/CORS errors
      if (isFetchError(e)) {
        addLog(`❌ FETCH FAILED (Merge): ${e.message}`, 'error');
        addLog(`   💡 ${getFetchErrorHint()}`, 'error');
        return { fetchFailed: true, reason: 'CORS_OR_NETWORK_ERROR' };
      }
      addLog(`⚠️ Merge: ${e.message}`, 'warning');
    }
    
    addLog('✅ Step 2 abgeschlossen!', 'success');
    return { fetchFailed: false }; // P0 (C5): Explicit success indicator
  };

  // Execute Step 3: Extract Claims → Approve → Merge → Promote to AKUs
  const executeStep3 = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    
    addLog('🚀 Step 3: Starte AKU/Claim-Workflow...', 'info');
    
    // 3a: Extract Claims from chunks
    addLog('📥 Extrahiere Claims aus Chunks...', 'info');
    let claimsExtractedInRun = 0;
    try {
      const extractRes = await fetch(
        `${API}/api/ontology-proposals/extract-claims`,
        { 
          method: 'POST', 
          headers,
          body: JSON.stringify({
            source_id: selectedSourceId,
            exam_profile_id: selectedExam,
            max_claims: 500,  // Erhöht von 100
            run_key: runKey
          })
        }
      );
      
      // P0.5 FIX: Check content-type - HARD STOP if HTML received
      const contentType = extractRes.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const rawText = await extractRes.text();
        const isHtml = rawText.trim().startsWith('<!DOCTYPE') || rawText.trim().startsWith('<html');
        addLog(`❌ NON_JSON_RESPONSE: Server lieferte ${contentType || 'unbekannt'} statt JSON`, 'error');
        if (isHtml) {
          addLog(`   📝 HTML empfangen (möglicherweise Timeout/Proxy-Fehler): ${rawText.substring(0, 80)}...`, 'error');
        }
        return { claimsCreated: 0, hardStop: true, reason: 'NON_JSON_RESPONSE' };
      }
      
      if (extractRes.ok) {
        const result = await extractRes.json();
        claimsExtractedInRun = result.claims_extracted || result.proposals_created || 0;
        const dedupeHits = result.dedupe_hits || 0;
        
        // P0: Show exam_profile_id used for diagnostics
        addLog(`   📋 exam_profile_id: ${result.exam_profile_id || selectedExam}`, 'info');
        if (result.staging_ontology) {
          addLog(`   📦 staging_ontology: ${result.staging_ontology.version_id}${result.staging_ontology.auto_created ? ' (auto-created)' : ''}`, 'info');
        }
        
        // IDEMPOTENT logging
        if (dedupeHits > 0) {
          addLog(`✅ ${claimsExtractedInRun} erstellt, ${dedupeHits} Duplikate übersprungen`, 'success');
        } else {
          addLog(`✅ ${claimsExtractedInRun} Claims extrahiert`, 'success');
        }
        if (result.admin_hint) {
          addLog(`ℹ️ ${result.admin_hint}`, 'info');
        }
        
        // P0 (Fix 4): HARD STOP if 0 claims AND 0 dedupe
        if (claimsExtractedInRun === 0 && dedupeHits === 0) {
          addLog('❌ KEINE CLAIMS EXTRAHIERT - Prüfen Sie Chunks und Source-Inhalt', 'error');
          return { claimsCreated: 0, hardStop: true, reason: 'NO_CLAIMS_EXTRACTED' };
        }
      } else {
        const errText = await extractRes.text();
        addLog(`⚠️ Claim-Extraktion: ${errText.substring(0, 100)}`, 'warning');
      }
    } catch (e) {
      addLog(`⚠️ Claim-Extraktion: ${e.message}`, 'warning');
    }
    
    // 3b: Submit Claims for review (run_key scoped)
    addLog('📤 Reiche Claims zur Review ein...', 'info');
    let totalSubmitted = 0;
    try {
      // Hole alle DRAFT Claims mit run_key Filter
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalSubmitted < 500) {
        const draftRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=DRAFT&run_key=${runKey}&page_size=200&page=${page}`,
          { headers }
        );
        const draftData = await draftRes.json();
        const draftIds = (draftData.items || []).map(p => p.proposal_id);
        
        if (draftIds.length > 0) {
          const submitRes = await fetch(`${API}/api/ontology-proposals/bulk/submit-for-review`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: draftIds, run_key: runKey })
          });
          if (submitRes.ok) {
            const result = await submitRes.json();
            totalSubmitted += result.modified_count || draftIds.length;
          }
        }
        
        hasMore = draftData.has_next || false;
        page++;
      }
      
      if (totalSubmitted > 0) {
        addLog(`✅ ${totalSubmitted} Claims submitted`, 'success');
      } else {
        addLog('ℹ️ Keine DRAFT Claims zum Submitten', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Submit Claims: ${e.message}`, 'warning');
    }
    
    // 3c: Approve Claims (run_key scoped)
    addLog('👍 Approve alle Claims...', 'info');
    let totalApproved = 0;
    try {
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalApproved < 500) {
        const reviewRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=REVIEW&run_key=${runKey}&page_size=200&page=${page}`,
          { headers }
        );
        const reviewData = await reviewRes.json();
        const reviewIds = (reviewData.items || []).map(p => p.proposal_id);
        
        if (reviewIds.length > 0) {
          const approveRes = await fetch(`${API}/api/ontology-proposals/bulk/approve`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: reviewIds, run_key: runKey })
          });
          if (approveRes.ok) {
            const result = await approveRes.json();
            totalApproved += result.approved_count || reviewIds.length;
          }
        }
        
        hasMore = reviewData.has_next || false;
        page++;
      }
      
      if (totalApproved > 0) {
        addLog(`✅ ${totalApproved} Claims approved`, 'success');
      } else {
        addLog('ℹ️ Keine REVIEW Claims zum Approven', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Approve Claims: ${e.message}`, 'warning');
    }
    
    // 3d: Merge Claims (run_key scoped)
    addLog('🔀 Merge alle Claims...', 'info');
    let totalMerged = 0;
    try {
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalMerged < 500) {
        const approvedRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=APPROVED&run_key=${runKey}&page_size=200&page=${page}`,
          { headers }
        );
        const approvedData = await approvedRes.json();
        const approvedIds = (approvedData.items || []).map(p => p.proposal_id);
        
        if (approvedIds.length > 0) {
          const mergeRes = await fetch(`${API}/api/ontology-proposals/bulk/merge`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: approvedIds, run_key: runKey })
          });
          if (mergeRes.ok) {
            const result = await mergeRes.json();
            totalMerged += result.merged_count || approvedIds.length;
          }
        }
        
        hasMore = approvedData.has_next || false;
        page++;
      }
      
      if (totalMerged > 0) {
        addLog(`✅ ${totalMerged} Claims gemerged`, 'success');
      } else {
        addLog('ℹ️ Keine APPROVED Claims zum Mergen', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Merge Claims: ${e.message}`, 'warning');
    }
    
    // 3e: Promote merged claims to AKUs
    addLog('⬆️ Promote Claims zu AKUs...', 'info');
    try {
      // P0 HOTFIX: Changed to POST with JSON body (was query params)
      const promoteUrl = `${API}/api/akus-v2/promote-all-merged`;
      const promoteRes = await fetch(promoteUrl, { 
        method: 'POST', 
        headers,
        body: JSON.stringify({
          exam_profile_id: selectedExam,
          include_non_effective: true,
          run_key: runKey || undefined
        })
      });
      
      // P0.5 FIX: Check content-type - HARD STOP if HTML received
      const contentType = promoteRes.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const rawText = await promoteRes.text();
        const isHtml = rawText.trim().startsWith('<!DOCTYPE') || rawText.trim().startsWith('<html');
        addLog(`❌ NON_JSON_RESPONSE: Promote lieferte ${contentType || 'unbekannt'} statt JSON`, 'error');
        if (isHtml) {
          addLog(`   📝 HTML empfangen (502/504 Timeout?): ${rawText.substring(0, 80)}...`, 'error');
        }
        // Don't return here, continue with workflow
      } else if (promoteRes.ok) {
        const result = await promoteRes.json();
        addLog(`✅ ${result.promoted_count || 0} AKUs erstellt`, 'success');
        
        // Zeige skipped_reasons falls vorhanden
        if (result.skipped_count > 0) {
          const reasons = result.skipped_reasons || {};
          if (reasons.dedupe > 0) addLog(`   ℹ️ ${reasons.dedupe} bereits vorhanden (dedupe)`, 'info');
          if (reasons.orphan_anchors > 0) addLog(`   ⚠️ ${reasons.orphan_anchors} ohne Concept-Anchors`, 'warning');
          if (reasons.non_effective > 0) addLog(`   ⚠️ ${reasons.non_effective} ohne Effective Source`, 'warning');
        }
        
        if (result.admin_hint) {
          addLog(`   💡 ${result.admin_hint}`, 'info');
        }
      } else {
        const errText = await promoteRes.text();
        addLog(`⚠️ Promotion: ${errText.substring(0, 100)}`, 'warning');
      }
    } catch (e) {
      addLog(`⚠️ Promotion: ${e.message}`, 'warning');
    }
    
    addLog('✅ Step 3 abgeschlossen!', 'success');
  };

  // P0 SYSTEMIC PATCH: Step 3 with outcome tracking
  const executeStep3WithOutcome = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let outcome = { akusCreated: 0, promotionFailed: false };
    
    addLog('🚀 Step 3: Starte AKU/Claim-Workflow...', 'info');
    
    // 3a: Extract Claims from chunks
    addLog('📥 Extrahiere Claims aus Chunks...', 'info');
    try {
      const extractRes = await fetch(
        `${API}/api/ontology-proposals/extract-claims`,
        { 
          method: 'POST', 
          headers,
          body: JSON.stringify({
            source_id: selectedSourceId,
            exam_profile_id: selectedExam,
            max_claims: 500,
            run_key: runKey
          })
        }
      );
      
      if (extractRes.ok) {
        const result = await extractRes.json();
        addLog(`✅ ${result.claims_extracted || result.proposals_created || 0} Claims extrahiert`, 'success');
        if (result.admin_hint) {
          addLog(`ℹ️ ${result.admin_hint}`, 'info');
        }
      } else {
        const errText = await extractRes.text();
        addLog(`⚠️ Claim-Extraktion: ${errText.substring(0, 100)}`, 'warning');
      }
    } catch (e) {
      addLog(`⚠️ Claim-Extraktion: ${e.message}`, 'warning');
    }
    
    // 3b: Submit Claims for review
    addLog('📤 Reiche Claims zur Review ein...', 'info');
    let totalSubmitted = 0;
    try {
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalSubmitted < 500) {
        const draftRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=DRAFT&page_size=200&page=${page}`,
          { headers }
        );
        const draftData = await draftRes.json();
        const draftIds = (draftData.items || []).map(p => p.proposal_id);
        
        if (draftIds.length > 0) {
          const submitRes = await fetch(`${API}/api/ontology-proposals/bulk/submit-for-review`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: draftIds, run_key: runKey })
          });
          if (submitRes.ok) {
            const result = await submitRes.json();
            totalSubmitted += result.modified_count || draftIds.length;
          }
        }
        
        hasMore = draftData.has_next || false;
        page++;
      }
      
      if (totalSubmitted > 0) {
        addLog(`✅ ${totalSubmitted} Claims submitted`, 'success');
      } else {
        addLog('ℹ️ Keine DRAFT Claims zum Submitten', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Submit Claims: ${e.message}`, 'warning');
    }
    
    // 3c: Approve Claims
    addLog('👍 Approve alle Claims...', 'info');
    let totalApproved = 0;
    try {
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalApproved < 500) {
        const reviewRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=REVIEW&page_size=200&page=${page}`,
          { headers }
        );
        const reviewData = await reviewRes.json();
        const reviewIds = (reviewData.items || []).map(p => p.proposal_id);
        
        if (reviewIds.length > 0) {
          const approveRes = await fetch(`${API}/api/ontology-proposals/bulk/approve`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: reviewIds, run_key: runKey })
          });
          if (approveRes.ok) {
            const result = await approveRes.json();
            totalApproved += result.approved_count || reviewIds.length;
          }
        }
        
        hasMore = reviewData.has_next || false;
        page++;
      }
      
      if (totalApproved > 0) {
        addLog(`✅ ${totalApproved} Claims approved`, 'success');
      } else {
        addLog('ℹ️ Keine REVIEW Claims zum Approven', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Approve Claims: ${e.message}`, 'warning');
    }
    
    // 3d: Merge Claims
    addLog('🔀 Merge alle Claims...', 'info');
    let totalMerged = 0;
    try {
      let page = 1;
      let hasMore = true;
      
      while (hasMore && totalMerged < 500) {
        const approvedRes = await fetch(
          `${API}/api/ontology-proposals?source_id=${selectedSourceId}&proposal_kind=CLAIM&status=APPROVED&page_size=200&page=${page}`,
          { headers }
        );
        const approvedData = await approvedRes.json();
        const approvedIds = (approvedData.items || []).map(p => p.proposal_id);
        
        if (approvedIds.length > 0) {
          const mergeRes = await fetch(`${API}/api/ontology-proposals/bulk/merge`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ proposal_ids: approvedIds, run_key: runKey })
          });
          if (mergeRes.ok) {
            const result = await mergeRes.json();
            totalMerged += result.merged_count || approvedIds.length;
          }
        }
        
        hasMore = approvedData.has_next || false;
        page++;
      }
      
      if (totalMerged > 0) {
        addLog(`✅ ${totalMerged} Claims gemerged`, 'success');
      } else {
        addLog('ℹ️ Keine APPROVED Claims zum Mergen', 'info');
      }
    } catch (e) {
      addLog(`⚠️ Merge Claims: ${e.message}`, 'warning');
    }
    
    // 3e: Promote merged claims to AKUs - TRACK OUTCOME
    addLog('⬆️ Promote Claims zu AKUs...', 'info');
    try {
      // P0 HOTFIX: Changed to POST with JSON body (fixes 502 timeout)
      const promoteUrl = `${API}/api/akus-v2/promote-all-merged`;
      addLog(`   📡 URL: /api/akus-v2/promote-all-merged (POST)`, 'info');
      
      const promoteRes = await fetch(promoteUrl, { 
        method: 'POST', 
        headers,
        body: JSON.stringify({
          exam_profile_id: selectedExam,
          include_non_effective: true,
          run_key: runKey
        })
      });
      
      // P0: Robust logging - check content-type before parsing
      const contentType = promoteRes.headers.get('content-type') || '';
      addLog(`   📊 HTTP ${promoteRes.status}, Content-Type: ${contentType.split(';')[0]}`, 
        promoteRes.ok ? 'info' : 'warning');
      
      // Handle HTML responses (auth redirect, proxy error, etc.)
      if (contentType.includes('text/html')) {
        outcome.promotionFailed = true;
        const htmlSnippet = (await promoteRes.text()).substring(0, 100);
        addLog(`   ❌ API returned HTML instead of JSON (likely 401/404/502 or redirect)`, 'error');
        addLog(`   📄 HTML snippet: ${htmlSnippet.replace(/\n/g, ' ').substring(0, 60)}...`, 'error');
        addLog(`   💡 Admin-Hint: Prüfen Sie Auth-Token, Base-URL, und Proxy-Konfiguration`, 'info');
        return outcome;
      }
      
      if (promoteRes.ok) {
        const result = await promoteRes.json();
        outcome.akusCreated = result.promoted_count || 0;
        addLog(`✅ ${outcome.akusCreated} AKUs erstellt`, outcome.akusCreated > 0 ? 'success' : 'warning');
        
        // Log skipped_reasons for diagnostics
        if (result.skipped_count > 0) {
          const reasons = result.skipped_reasons || {};
          if (reasons.dedupe > 0) addLog(`   ℹ️ ${reasons.dedupe} bereits vorhanden (dedupe)`, 'info');
          if (reasons.orphan_anchors > 0) addLog(`   ⚠️ ${reasons.orphan_anchors} ohne Concept-Anchors`, 'warning');
          if (reasons.non_effective > 0) addLog(`   ⚠️ ${reasons.non_effective} ohne Effective Source`, 'warning');
          if (reasons.status_not_allowed > 0) addLog(`   ⚠️ ${reasons.status_not_allowed} falscher Status (nicht MERGED)`, 'warning');
        }
        
        if (result.admin_hint) {
          addLog(`   💡 ${result.admin_hint}`, 'info');
        }
        
        // P0: Mark promotion as failed if 0 AKUs created
        if (outcome.akusCreated === 0) {
          outcome.promotionFailed = true;
          addLog('   ❌ PROMOTION FEHLGESCHLAGEN: 0 AKUs erstellt', 'error');
          
          // B1: Show failed_reasons_histogram for actionable debugging
          if (result.failed_reasons_histogram && Object.keys(result.failed_reasons_histogram).length > 0) {
            addLog('   📊 Failed Reasons Histogram:', 'error');
            for (const [reason, count] of Object.entries(result.failed_reasons_histogram)) {
              addLog(`      ${reason}: ${count}`, 'error');
            }
          }
          
          // B1: Show sample failed claims (first 3)
          if (result.sample_failed_claims) {
            for (const [reason, samples] of Object.entries(result.sample_failed_claims)) {
              if (samples && samples.length > 0) {
                addLog(`   📋 Samples für ${reason}:`, 'info');
                samples.slice(0, 3).forEach(s => {
                  addLog(`      - ${s.proposal_id}: ${s.reason?.substring(0, 80)}`, 'info');
                });
              }
            }
          }
          
          // B2: Show environment for cross-env debugging
          if (result.environment) {
            addLog(`   🌐 Environment: ${result.environment.env} (${result.environment.db_name})`, 'info');
          }
        } else {
          // Auto-link concepts to enable Rack building
          addLog('🔗 Auto-Linking AKU Concepts...', 'info');
          try {
            const linkRes = await fetch(
              `${API}/api/akus-v2/auto-link-concepts?exam_profile_id=${selectedExam}`,
              { method: 'POST', headers }
            );
            if (linkRes.ok) {
              const linkResult = await linkRes.json();
              addLog(`✅ ${linkResult.edges_created} Edges erstellt für ${linkResult.anchors_found} Concepts`, 'success');
            } else {
              addLog('⚠️ Auto-Link fehlgeschlagen (Rack-Build kann trotzdem funktionieren)', 'warning');
            }
          } catch (linkErr) {
            addLog(`⚠️ Auto-Link: ${linkErr.message}`, 'warning');
          }
        }
      } else {
        // Non-OK response with JSON content-type
        outcome.promotionFailed = true;
        try {
          const errJson = await promoteRes.json();
          const errMsg = errJson.detail || errJson.message || errJson.admin_hint || JSON.stringify(errJson).substring(0, 100);
          addLog(`❌ Promotion fehlgeschlagen: ${errMsg}`, 'error');
          if (errJson.claims_by_status) {
            addLog(`   📊 Claims Status: DRAFT=${errJson.claims_by_status.draft}, MERGED=${errJson.claims_by_status.merged}`, 'info');
          }
          if (errJson.admin_hint && errJson.admin_hint !== errMsg) {
            addLog(`   💡 ${errJson.admin_hint}`, 'info');
          }
        } catch (jsonErr) {
          const errText = await promoteRes.text();
          addLog(`❌ Promotion fehlgeschlagen (${promoteRes.status}): ${errText.substring(0, 100)}`, 'error');
        }
      }
    } catch (e) {
      outcome.promotionFailed = true;
      addLog(`❌ Promotion Network-Fehler: ${e.message}`, 'error');
      addLog(`   💡 Prüfen Sie Netzwerkverbindung und API-Verfügbarkeit`, 'info');
    }
    
    addLog(`✅ Step 3 abgeschlossen (${outcome.akusCreated} AKUs)`, outcome.akusCreated > 0 ? 'success' : 'warning');
    return outcome;
  };

  // P0 SYSTEMIC PATCH: Step 4 with outcome tracking
  const executeStep4WithOutcome = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let outcome = { racksCreated: 0, rackFailed: false };
    
    addLog('🚀 Step 4: Erstelle Rack...', 'info');
    
    // Get available AKUs
    const akusRes = await fetch(
      `${API}/api/akus-v2?exam_profile_id=${selectedExam}&status=DRAFT&limit=10`,
      { headers }
    );
    const akusData = await akusRes.json();
    const akus = akusData.items || [];
    
    if (akus.length === 0) {
      addLog('❌ Keine AKUs verfügbar für Rack-Erstellung', 'error');
      outcome.rackFailed = true;
      return outcome;
    }
    
    const seedAku = akus[0];
    addLog(`🎯 Seed AKU: ${seedAku.aku_id}`, 'info');
    
    try {
      const rackRes = await fetch(`${API}/api/racks/build`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          exam_profile_id: selectedExam,
          seed_aku_id: seedAku.aku_id,
          depth: 2,
          include_similar: true,
          include_contrasts: true,
          run_key: runKey
        })
      });
      
      if (rackRes.ok) {
        const result = await rackRes.json();
        if (result.gate_passed === false) {
          // Gate blocked
          addLog(`❌ Rack-Gate blockiert: ${result.reason_code}`, 'error');
          if (result.admin_hint) addLog(`   💡 ${result.admin_hint}`, 'info');
          outcome.rackFailed = true;
        } else {
          outcome.racksCreated = 1;
          addLog(`✅ Rack erstellt: ${result.rack_id}`, 'success');
          addLog(`   Focus Concepts: ${result.focus_concepts_count}, AKUs: ${result.source_akus_count}`, 'info');
        }
      } else {
        outcome.rackFailed = true;
        const errText = await rackRes.text();
        addLog(`❌ Rack-Erstellung fehlgeschlagen: ${errText.substring(0, 100)}`, 'error');
      }
    } catch (e) {
      outcome.rackFailed = true;
      addLog(`❌ Rack-Erstellung: ${e.message}`, 'error');
    }
    
    addLog(`✅ Step 4 abgeschlossen (${outcome.racksCreated} Racks)`, outcome.racksCreated > 0 ? 'success' : 'warning');
    return outcome;
  };

  // P0 SYSTEMIC PATCH: Step 5 with outcome tracking
  const executeStep5WithOutcome = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let outcome = { itemsGenerated: 0, generateFailed: false };
    
    addLog('🚀 Step 5: Generiere Items...', 'info');
    
    // Get available racks
    const racksRes = await fetch(
      `${API}/api/racks?exam_profile_id=${selectedExam}&status=ACTIVE&limit=1`,
      { headers }
    );
    const racksData = await racksRes.json();
    const racks = racksData.items || [];
    
    if (racks.length === 0) {
      addLog('❌ Keine Racks verfügbar für Item-Generierung', 'error');
      outcome.generateFailed = true;
      return outcome;
    }
    
    const rack = racks[0];
    addLog(`🎯 Rack: ${rack.rack_id}`, 'info');
    
    try {
      const genRes = await fetch(`${API}/api/generate/from-rack`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rack_id: rack.rack_id,
          exam_profile_id: selectedExam,
          count: 10,
          run_key: runKey
        })
      });
      
      if (genRes.ok) {
        const result = await genRes.json();
        if (result.gate_passed === false) {
          // Gate blocked
          addLog(`❌ Generate-Gate blockiert: ${result.reason_code}`, 'error');
          if (result.admin_hint) addLog(`   💡 ${result.admin_hint}`, 'info');
          outcome.generateFailed = true;
        } else {
          outcome.itemsGenerated = result.items_generated || result.items?.length || 0;
          addLog(`✅ ${outcome.itemsGenerated} Items generiert`, outcome.itemsGenerated > 0 ? 'success' : 'warning');
          if (result.warnings?.length > 0) {
            result.warnings.forEach(w => addLog(`   ⚠️ ${w}`, 'warning'));
          }
        }
      } else {
        outcome.generateFailed = true;
        const errText = await genRes.text();
        addLog(`❌ Item-Generierung fehlgeschlagen: ${errText.substring(0, 100)}`, 'error');
      }
    } catch (e) {
      outcome.generateFailed = true;
      addLog(`❌ Item-Generierung: ${e.message}`, 'error');
    }
    
    addLog(`✅ Step 5 abgeschlossen (${outcome.itemsGenerated} Items)`, outcome.itemsGenerated > 0 ? 'success' : 'warning');
    return outcome;
  };

  // P0 SYSTEMIC PATCH: Step 6 with outcome tracking
  const executeStep6WithOutcome = async (runKey) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let outcome = { itemsValidated: 0 };
    
    addLog('🚀 Step 6: Starte Validierungs-Pipeline...', 'info');
    
    // P0 (B4): run_key is REQUIRED - no fallback to "validate all drafts"
    if (!runKey) {
      addLog('❌ FEHLER: run_key fehlt - Validation abgebrochen', 'error');
      addLog('   💡 run_key ist erforderlich für run-scoped Validation', 'info');
      return outcome;
    }
    
    addLog(`🔑 Validiere Items für run_key: ${runKey}`, 'info');
    
    try {
      // Get items for this run - P0 FIX: Use correct endpoint
      const itemsRes = await fetch(
        `${API}/api/generate/items?exam_profile_id=${selectedExam}&run_key=${runKey}&status=DRAFT&limit=100`,
        { headers }
      );
      const itemsData = await itemsRes.json();
      const items = itemsData.items || [];
      
      if (items.length === 0) {
        addLog('ℹ️ Keine Items für diesen run_key gefunden', 'info');
        return outcome;
      }
      
      addLog(`📋 ${items.length} Items für Validation gefunden`, 'info');
      
      // Run validation pipeline
      const validateRes = await fetch(`${API}/api/validation/pipeline/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          exam_profile_id: selectedExam,
          run_key: runKey
        })
      });
      
      if (validateRes.ok) {
        const result = await validateRes.json();
        outcome.itemsValidated = result.validated_count || result.items_validated || items.length;
        addLog(`✅ ${outcome.itemsValidated} Items validiert`, 'success');
        
        if (result.passed_count) addLog(`   ✅ ${result.passed_count} bestanden`, 'success');
        if (result.failed_count) addLog(`   ❌ ${result.failed_count} fehlgeschlagen`, 'warning');
        if (result.review_count) addLog(`   🔍 ${result.review_count} zur Review`, 'info');
      } else {
        const errText = await validateRes.text();
        addLog(`⚠️ Validation: ${errText.substring(0, 100)}`, 'warning');
      }
    } catch (e) {
      addLog(`⚠️ Validation: ${e.message}`, 'warning');
    }
    
    addLog(`✅ Step 6 abgeschlossen (${outcome.itemsValidated} validiert)`, outcome.itemsValidated > 0 ? 'success' : 'warning');
    return outcome;
  };

  // Execute Step 4: Build Rack (Legacy - kept for manual mode)
  const executeStep4 = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    
    addLog('🚀 Step 4: Erstelle Rack...', 'info');
    
    // Get available AKUs
    const akusRes = await fetch(
      `${API}/api/akus-v2?exam_profile_id=${selectedExam}&status=DRAFT&limit=10`,
      { headers }
    );
    const akusData = await akusRes.json();
    const akus = akusData.items || [];
    
    if (akus.length === 0) {
      addLog('❌ Keine AKUs verfügbar für Rack-Erstellung', 'error');
      return;
    }
    
    const seedAku = akus[0];
    addLog(`🎯 Seed AKU: ${seedAku.aku_id}`, 'info');
    
    try {
      const rackRes = await fetch(`${API}/api/racks/build`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          exam_profile_id: selectedExam,
          seed_aku_id: seedAku.aku_id,
          depth: 2,
          include_similar: true,
          include_contrasts: true,
          run_key: runKey
        })
      });
      
      if (rackRes.ok) {
        const result = await rackRes.json();
        addLog(`✅ Rack erstellt: ${result.rack_id}`, 'success');
        addLog(`   Focus Concepts: ${result.focus_concepts_count}, AKUs: ${result.source_akus_count}`, 'info');
      } else {
        const errText = await rackRes.text();
        addLog(`❌ Rack-Erstellung fehlgeschlagen: ${errText.substring(0, 100)}`, 'error');
      }
    } catch (e) {
      addLog(`❌ Rack-Erstellung: ${e.message}`, 'error');
    }
    
    addLog('✅ Step 4 abgeschlossen!', 'success');
  };

  // Execute Step 5: Generate Items
  const executeStep5 = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    
    addLog('🚀 Step 5: Generiere Items...', 'info');
    
    // Get available rack
    const racksRes = await fetch(
      `${API}/api/racks?exam_profile_id=${selectedExam}&limit=5`,
      { headers }
    );
    const racksData = await racksRes.json();
    const racks = racksData.items || [];
    
    if (racks.length === 0) {
      addLog('❌ Keine Racks verfügbar für Item-Generierung', 'error');
      return;
    }
    
    const rack = racks[0];
    addLog(`🎯 Verwende Rack: ${rack.rack_id}`, 'info');
    
    // Speichere generierte Item-IDs für run_key Filterung
    let generatedIds = [];
    
    try {
      const genRes = await fetch(`${API}/api/generate/from-rack`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rack_id: rack.rack_id,
          exam_profile_id: selectedExam,
          question_count: 1,
          language: 'de',
          run_key: runKey
        })
      });
      
      if (genRes.ok) {
        const result = await genRes.json();
        addLog(`✅ ${result.items_generated || 1} Item(s) generiert!`, 'success');
        
        // Speichere Item-IDs für Validation
        if (result.items) {
          generatedIds = result.items.map(i => i.item_id);
          setGeneratedItemIds(prev => [...prev, ...generatedIds]);
        }
        
        if (result.items && result.items[0]) {
          const item = result.items[0];
          addLog(`📝 Frage: "${(item.question_text || '').substring(0, 80)}..."`, 'info');
        }
      } else {
        const errText = await genRes.text();
        addLog(`❌ Item-Generierung fehlgeschlagen: ${errText.substring(0, 100)}`, 'error');
      }
    } catch (e) {
      addLog(`❌ Item-Generierung: ${e.message}`, 'error');
    }
    
    addLog('✅ Step 5 abgeschlossen!', 'success');
  };

  // Execute Step 6: Validate Generated Items (run_key scoped)
  const executeStep6 = async (runKey = null) => {
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    
    addLog('🚀 Step 6: Starte Validierungs-Pipeline...', 'info');
    
    // B7: run_key-Filterung - nur Items aus diesem Run validieren
    let itemsToValidate = [];
    
    if (generatedItemIds.length > 0) {
      // Bevorzugt: Items aus diesem Run (via generatedItemIds State)
      addLog(`🔑 Validiere nur Items aus diesem Run (${generatedItemIds.length} IDs)`, 'info');
      
      // Hole diese spezifischen Items
      const itemsRes = await fetch(
        `${API}/api/generate/items?exam_profile_id=${selectedExam}&limit=50`,
        { headers }
      );
      const itemsData = await itemsRes.json();
      const allItems = itemsData.items || [];
      
      // Filtere auf unsere generierten IDs
      itemsToValidate = allItems.filter(i => 
        generatedItemIds.includes(i.item_id) && i.status === 'DRAFT'
      );
    } else {
      // Fallback: Alle DRAFT Items (mit Warnung)
      addLog('⚠️ Kein run_key - validiere alle DRAFT Items', 'warning');
      const itemsRes = await fetch(
        `${API}/api/generate/items?exam_profile_id=${selectedExam}&status=DRAFT&limit=50`,
        { headers }
      );
      const itemsData = await itemsRes.json();
      itemsToValidate = itemsData.items || [];
    }
    
    if (itemsToValidate.length === 0) {
      addLog('ℹ️ Keine Items zur Validierung ausstehend', 'info');
      addLog('✅ Step 6 abgeschlossen!', 'success');
      return;
    }
    
    addLog(`📋 ${itemsToValidate.length} Item(s) zur Validierung`, 'info');
    
    let validatedCount = 0;
    let approvedCount = 0;
    let reviewCount = 0;
    let rejectedCount = 0;
    
    for (const item of itemsToValidate) {
      try {
        addLog(`🔍 Validiere Item: ${item.item_id}...`, 'info');
        
        // Build question object for validation
        const questionObj = {
          question_id: item.item_id,
          question_text: item.question_text,
          correct_answer: item.correct_answer,
          distractors: item.distractors || [],
          choices: [
            { id: 'correct', text: item.correct_answer, isCorrect: true },
            ...(item.distractors || []).map((d, i) => ({ id: `d${i}`, text: d, isCorrect: false }))
          ],
          difficulty: item.difficulty_band || 'MEDIUM'
        };
        
        // Run full validation pipeline
        const valRes = await fetch(`${API}/api/validation/full-pipeline`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            question: questionObj,
            certification_id: selectedExam,
            validators: ['all'],
            run_key: runKey
          })
        });
        
        if (valRes.ok) {
          const valResult = await valRes.json();
          
          // Check if gate passed
          if (!valResult.gate_passed) {
            addLog(`⚠️ Gate blockiert: ${valResult.gate_failure_reason || 'Unbekannt'}`, 'warning');
            if (valResult.admin_hint) {
              addLog(`💡 Hinweis: ${valResult.admin_hint[0] || ''}`, 'info');
            }
            continue;
          }
          
          const recommendation = valResult.summary?.recommendation || 'review';
          const overallScore = valResult.summary?.overall_score || 0;
          
          // Update item with validation result
          await fetch(`${API}/api/generate/items/${item.item_id}/validate`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              validation_result: valResult,
              validation_status: 'completed',
              overall_score: overallScore,
              recommendation: recommendation,
              run_key: runKey
            })
          }).catch(() => {
            // If dedicated endpoint doesn't exist, we'll handle it differently
          });
          
          validatedCount++;
          
          // Auto-approve if score >= 85
          if (recommendation === 'approve' || overallScore >= 85) {
            try {
              await fetch(`${API}/api/generate/items/${item.item_id}/approve`, {
                method: 'POST',
                headers
              });
              approvedCount++;
              addLog(`✅ Item ${item.item_id}: Score ${overallScore.toFixed(1)} → AUTO-GENEHMIGT`, 'success');
            } catch (e) {
              addLog(`✅ Item ${item.item_id}: Score ${overallScore.toFixed(1)} → Empfohlen: Genehmigen`, 'success');
            }
          } else if (recommendation === 'review' || (overallScore >= 60 && overallScore < 85)) {
            reviewCount++;
            addLog(`🔶 Item ${item.item_id}: Score ${overallScore.toFixed(1)} → Manuelle Prüfung empfohlen`, 'warning');
          } else {
            rejectedCount++;
            addLog(`❌ Item ${item.item_id}: Score ${overallScore.toFixed(1)} → Ablehnung empfohlen`, 'error');
          }
          
          // Log individual stage scores
          const scores = valResult.summary?.scores || {};
          const stageInfo = Object.entries(scores)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ');
          if (stageInfo) {
            addLog(`   Scores: ${stageInfo}`, 'info');
          }
          
        } else {
          const errText = await valRes.text();
          addLog(`⚠️ Validierung für ${item.item_id} fehlgeschlagen: ${errText.substring(0, 80)}`, 'warning');
        }
        
      } catch (e) {
        addLog(`⚠️ Fehler bei ${item.item_id}: ${e.message}`, 'warning');
      }
      
      // Small delay between validations
      await new Promise(r => setTimeout(r, 300));
    }
    
    addLog('─'.repeat(40), 'info');
    addLog(`📊 Validierungs-Ergebnis:`, 'info');
    addLog(`   ✅ Genehmigt: ${approvedCount}`, approvedCount > 0 ? 'success' : 'info');
    addLog(`   🔶 Zur Prüfung: ${reviewCount}`, reviewCount > 0 ? 'warning' : 'info');
    addLog(`   ❌ Abgelehnt: ${rejectedCount}`, rejectedCount > 0 ? 'error' : 'info');
    addLog(`   📋 Gesamt validiert: ${validatedCount}/${itemsToValidate.length}`, 'info');
    addLog('✅ Step 6 abgeschlossen!', 'success');
  };

  // Execute current step
  const executeCurrentStep = async () => {
    if (executing) return;
    
    setExecuting(true);
    setExecutionLog([]);
    
    try {
      switch (currentStep) {
        case 1:
          // Step 1 is just source selection - move to next
          if (selectedSourceId) {
            addLog(`✅ Source ausgewählt: ${selectedSourceId}`, 'success');
            setCurrentStep(2);
          } else {
            toast.error('Bitte wählen Sie eine Source aus');
          }
          break;
        case 2:
          await executeStep2(currentRunKey);
          setCurrentStep(3);
          break;
        case 3:
          await executeStep3();
          setCurrentStep(4);
          break;
        case 4:
          await executeStep4();
          setCurrentStep(5);
          break;
        case 5:
          await executeStep5();
          setCurrentStep(6);
          break;
        case 6:
          await executeStep6();
          break;
        default:
          break;
      }
      
      // Refresh statuses
      await refreshStatuses();
      
    } catch (error) {
      console.error('Step execution failed:', error);
      addLog(`❌ Fehler: ${error.message}`, 'error');
      toast.error(`Fehler: ${error.message}`);
    } finally {
      setExecuting(false);
    }
  };

  // Run ALL steps automatically
  const runAllSteps = async () => {
    if (executing || !selectedSourceId) {
      toast.error('Bitte wählen Sie zuerst eine Source aus');
      return;
    }
    
    // P0 FIX: Validate that the selected source matches the selected exam
    const selectedSource = sources.find(s => s.source_id === selectedSourceId);
    const sourceExamIds = selectedSource?.exam_ids || [];
    
    if (!sourceExamIds.includes(selectedExam)) {
      toast.error(
        `❌ Source passt nicht zum Exam!\n\n` +
        `Die ausgewählte Source "${selectedSource?.title || selectedSourceId}" ist für folgende Exams registriert: ${sourceExamIds.join(', ') || 'Keine'}.\n\n` +
        `Sie haben aber "${selectedExam}" ausgewählt.\n\n` +
        `Bitte wählen Sie eine passende Source oder ändern Sie das Exam.`
      );
      return;
    }
    
    const chunkCount = selectedSource?.chunk_count || 0;
    
    if (chunkCount > 50) {
      const confirmed = window.confirm(
        `⚠️ Große Operation\n\n` +
        `Diese Source hat ${chunkCount} Chunks.\n` +
        `Der Workflow wird viele Proposals, Claims und AKUs erstellen.\n\n` +
        `Sind Sie sicher, dass Sie fortfahren möchten?`
      );
      if (!confirmed) {
        toast.info('Workflow abgebrochen');
        return;
      }
    }
    
    setExecuting(true);
    setExecutionLog([]);
    setCurrentStep(1);
    
    // P1.8: Generate systemweiter run_key für diesen Workflow
    const runKey = `hp_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 6)}`;
    setCurrentRunKey(runKey);
    
    // Run-Key Policy: Save to history (max 20)
    setRunKeyHistory(prev => {
      const updated = [{ key: runKey, exam: selectedExam, source: selectedSourceId, timestamp: new Date().toISOString() }, ...prev];
      return updated.slice(0, 20); // Keep only last 20
    });
    setLastRunKey(runKey);
    
    // P0 SYSTEMIC PATCH (F): Track outcomes for proper status determination
    let workflowOutcome = {
      akusCreated: 0,
      racksCreated: 0,
      itemsGenerated: 0,
      itemsValidated: 0,
      promotionFailed: false,
      rackFailed: false,
      generateFailed: false
    };
    
    try {
      addLog('🚀 STARTE VOLLAUTOMATISCHEN WORKFLOW (6 Schritte)...', 'info');
      addLog(`📁 Source: ${selectedSourceId}`, 'info');
      addLog(`📋 Exam: ${selectedExam}`, 'info');
      addLog(`🔑 Run-Key: ${runKey}`, 'info');
      addLog('─'.repeat(40), 'info');
      
      // Step 1: Already have source
      addLog('✅ Step 1: Source ausgewählt', 'success');
      
      // P2: Check source gates
      addLog('🛡️ Prüfe Source Gates (S1, S2)...', 'info');
      const sourceGates = await checkSourceGates(selectedSourceId);
      if (sourceGates) {
        addLog(`   S1: ${sourceGates.s1_registration?.status || 'N/A'}`, 
          sourceGates.s1_registration?.status === 'PASSED' ? 'success' : 'warning');
        addLog(`   S2: ${sourceGates.s2_evidence?.status || 'N/A'}`,
          sourceGates.s2_evidence?.status === 'PASSED' ? 'success' : 'warning');
      }
      
      // Step 2: Proposals
      setCurrentStep(2);
      const step2Result = await executeStep2(runKey);
      
      // P0 (C5): HARD STOP if fetch failed (CORS/network error)
      if (step2Result?.fetchFailed) {
        addLog('─'.repeat(40), 'info');
        addLog('❌ WORKFLOW ABGEBROCHEN - FETCH/CORS FEHLER', 'error');
        addLog(`   reason_code: ${step2Result.reason || 'FETCH_FAILED'}`, 'error');
        addLog('   admin_hint: Prüfen Sie die API-Konfiguration und Netzwerkverbindung', 'error');
        toast.error('Workflow abgebrochen: API-Verbindungsfehler');
        return;
      }
      await new Promise(r => setTimeout(r, 500));
      
      // Step 3: Claims & AKUs - NOW RETURNS outcome
      setCurrentStep(3);
      const step3Result = await executeStep3WithOutcome(runKey);
      workflowOutcome.akusCreated = step3Result.akusCreated;
      workflowOutcome.promotionFailed = step3Result.promotionFailed;
      await new Promise(r => setTimeout(r, 500));
      
      // P0 (F1): Step 4 runs ONLY if AKUs > 0
      setCurrentStep(4);
      if (workflowOutcome.akusCreated > 0) {
        const step4Result = await executeStep4WithOutcome(runKey);
        workflowOutcome.racksCreated = step4Result.racksCreated;
        workflowOutcome.rackFailed = step4Result.rackFailed;
      } else {
        addLog('❌ Step 4 ÜBERSPRUNGEN: Keine AKUs verfügbar (0 erstellt)', 'error');
        workflowOutcome.rackFailed = true;
      }
      await new Promise(r => setTimeout(r, 500));
      
      // P0 (F2): Step 5 runs ONLY if Racks > 0
      setCurrentStep(5);
      if (workflowOutcome.racksCreated > 0) {
        const step5Result = await executeStep5WithOutcome(runKey);
        workflowOutcome.itemsGenerated = step5Result.itemsGenerated;
        workflowOutcome.generateFailed = step5Result.generateFailed;
      } else {
        addLog('❌ Step 5 ÜBERSPRUNGEN: Keine Racks verfügbar (0 erstellt)', 'error');
        workflowOutcome.generateFailed = true;
      }
      await new Promise(r => setTimeout(r, 500));
      
      // P0 (F3): Step 6 runs ONLY if Items > 0
      setCurrentStep(6);
      if (workflowOutcome.itemsGenerated > 0) {
        const step6Result = await executeStep6WithOutcome(runKey);
        workflowOutcome.itemsValidated = step6Result.itemsValidated;
      } else {
        addLog('❌ Step 6 ÜBERSPRUNGEN: Keine Items generiert (0 erstellt)', 'error');
      }
      
      // P0 (F4): Determine FINAL STATUS based on OUTCOME
      addLog('─'.repeat(40), 'info');
      
      if (workflowOutcome.itemsGenerated > 0 && workflowOutcome.itemsValidated > 0) {
        // TRUE SUCCESS
        addLog('🎉 WORKFLOW ERFOLGREICH ABGESCHLOSSEN!', 'success');
        addLog(`   ✅ ${workflowOutcome.akusCreated} AKUs erstellt`, 'success');
        addLog(`   ✅ ${workflowOutcome.racksCreated} Racks erstellt`, 'success');
        addLog(`   ✅ ${workflowOutcome.itemsGenerated} Items generiert`, 'success');
        addLog(`   ✅ ${workflowOutcome.itemsValidated} Items validiert`, 'success');
        addLog(`   Run-Key: ${runKey}`, 'info');
        toast.success(`Workflow erfolgreich! ${workflowOutcome.itemsGenerated} Items generiert.`);
      } else if (workflowOutcome.itemsGenerated > 0) {
        // PARTIAL - Generated but not validated
        addLog('⚠️ WORKFLOW TEILWEISE ABGESCHLOSSEN', 'warning');
        addLog(`   ✅ ${workflowOutcome.itemsGenerated} Items generiert`, 'success');
        addLog(`   ⚠️ Validation fehlgeschlagen oder keine Items validiert`, 'warning');
        addLog(`   Run-Key: ${runKey}`, 'info');
        toast.warning('Workflow teilweise abgeschlossen. Items generiert, aber nicht validiert.');
      } else {
        // FAILED - No items generated
        addLog('❌ WORKFLOW FEHLGESCHLAGEN - KEINE ITEMS GENERIERT', 'error');
        addLog(`   📊 Ergebnis: ${workflowOutcome.akusCreated} AKUs, ${workflowOutcome.racksCreated} Racks, 0 Items`, 'error');
        
        // Provide diagnostic hints
        if (workflowOutcome.akusCreated === 0) {
          addLog('   💡 Promotion hat 0 AKUs erstellt. Prüfen Sie:', 'info');
          // B1: Build diagnostics URL from current origin
          const diagnosticsUrl = `${window.location.origin}/api/runs/${runKey}/diagnostics`;
          addLog(`      GET ${diagnosticsUrl} für Details`, 'info');
        } else if (workflowOutcome.racksCreated === 0) {
          addLog('   💡 Rack-Build fehlgeschlagen. Prüfen Sie:', 'info');
          addLog('      - Mindestens 3 AKUs mit kanonischen Concept-Anchors nötig', 'info');
        }
        
        addLog(`   Run-Key: ${runKey}`, 'info');
        toast.error('Workflow fehlgeschlagen. Keine Items generiert. Prüfen Sie die Logs.');
      }
      
    } catch (error) {
      addLog(`❌ Workflow abgebrochen: ${error.message}`, 'error');
      toast.error(`Workflow abgebrochen: ${error.message}`);
    } finally {
      setExecuting(false);
      await refreshStatuses();
    }
  };

  // Initial load
  useEffect(() => {
    if (isActive) {
      fetchCourses();
      fetchExamProfiles();
      fetchSources();
    }
  }, [isActive, fetchCourses, fetchExamProfiles, fetchSources]);

  // P0 FIX: Refresh sources when exam changes to filter by selected exam
  useEffect(() => {
    if (isActive && selectedExam) {
      // Clear current source selection when exam changes
      setSelectedSourceId('');
      // P0 FIX: Reset step 1 to loading state while fetching new sources
      setStepStatus(prev => ({
        ...prev,
        1: { status: StepStatus.BLOCKED, hint: `Lade Sources für ${selectedExam}...`, data: null }
      }));
      fetchSources();
    }
  }, [selectedExam, isActive, fetchSources]);

  // Refresh when source changes
  useEffect(() => {
    if (isActive && selectedSourceId) {
      refreshStatuses();
    }
  }, [selectedSourceId, isActive]);

  // Calculate progress - Now 6 steps
  const completedSteps = Object.values(stepStatus).filter(s => s.status === StepStatus.COMPLETE).length;
  const progressPercent = (completedSteps / 6) * 100;

  return (
    <div className="space-y-6" data-testid="happy-path-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Rocket className="w-6 h-6 text-orange-500" />
            Guided Mode
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Automatisierter 6-Schritt-Workflow: Source → Proposals → AKUs → Rack → Generate → Validate
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Course Selector (P0.2) */}
          <Select value={selectedCourse} onValueChange={handleCourseChange}>
            <SelectTrigger className="w-32" data-testid="course-select">
              <SelectValue placeholder="Kurs" />
            </SelectTrigger>
            <SelectContent>
              {courses.map((course) => (
                <SelectItem key={course.code} value={course.code}>
                  {course.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Exam Profile Selector (only allowed profiles for selected course) */}
          <Select value={selectedExam} onValueChange={setSelectedExam}>
            <SelectTrigger className="w-40" data-testid="exam-profile-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {examProfiles
                .filter(exam => {
                  // If course selected, only show allowed profiles
                  const course = courses.find(c => c.code === selectedCourse);
                  if (course?.allowed_exam_profiles?.length > 0) {
                    return course.allowed_exam_profiles.includes(exam.exam_id);
                  }
                  return true;  // Show all if no course selected
                })
                .map((exam) => (
                  <SelectItem key={exam.exam_id} value={exam.exam_id}>
                    {exam.exam_id}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={refreshStatuses}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Progress */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Fortschritt: {completedSteps}/6 Schritte</span>
            <span className="text-sm font-bold text-blue-600">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-3" />
        </CardContent>
      </Card>

      {/* Source Selection - Step 1 */}
      <Card className={currentStep === 1 ? 'ring-2 ring-blue-500' : ''}>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                stepStatus[1].status === StepStatus.COMPLETE ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
              }`}>
                {stepStatus[1].status === StepStatus.COMPLETE ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-600" />
                  Source auswählen
                </CardTitle>
                <p className="text-sm text-gray-500">{stepStatus[1].hint}</p>
              </div>
            </div>
            <Badge className={STATUS_CONFIG[stepStatus[1].status].color}>
              {STATUS_CONFIG[stepStatus[1].status].badgeText}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0 pb-4">
          <div className="flex items-center gap-3">
            <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
              <SelectTrigger className="flex-1" data-testid="source-select">
                <SelectValue placeholder="Select source with chunks..." />
              </SelectTrigger>
              <SelectContent>
                {sources.map((source) => (
                  <SelectItem key={source.source_id} value={source.source_id}>
                    {source.title || source.canonical_name || source.source_id} ({source.chunk_count} Chunks)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Refresh button to reload sources */}
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchSources}
              title="Refresh sources"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {sources.length === 0 && (
              <Button variant="outline" onClick={() => onNavigateToTab?.('sources')}>
                Add Source
              </Button>
            )}
          </div>
          {/* P0 FIX: Warning when no sources match selected exam */}
          {sources.length === 0 && selectedExam && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Keine Sources für "{selectedExam}" gefunden</p>
                  <p className="text-amber-700 mt-1">
                    Bitte registrieren Sie eine Source für dieses Exam oder wählen Sie ein anderes Exam.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* P2: Drilldown Navigation */}
          {selectedSourceId && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onNavigateToEvidence?.(selectedSourceId)}
                className="text-xs text-gray-600 hover:text-blue-600"
              >
                <Eye className="w-3 h-3 mr-1" />
                Evidence anzeigen
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onNavigateToChunks?.(selectedSourceId)}
                className="text-xs text-gray-600 hover:text-blue-600"
              >
                <Layers className="w-3 h-3 mr-1" />
                Chunks anzeigen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Steps 2-6 */}
      {[
        { step: 2, title: 'Proposals', icon: Network, color: 'purple' },
        { step: 3, title: 'AKUs/Claims', icon: Layers, color: 'yellow' },
        { step: 4, title: 'Rack', icon: FileText, color: 'indigo' },
        { step: 5, title: 'Generieren', icon: Sparkles, color: 'orange' },
        { step: 6, title: 'Validieren', icon: Shield, color: 'emerald' }
      ].map(({ step, title, icon: Icon, color }) => (
        <React.Fragment key={step}>
          <div className="flex justify-center">
            <ArrowDown className="w-5 h-5 text-gray-300" />
          </div>
          <Card className={currentStep === step ? 'ring-2 ring-blue-500' : ''}>
            <CardHeader className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    stepStatus[step].status === StepStatus.COMPLETE ? 'bg-green-600 text-white' :
                    stepStatus[step].status === StepStatus.IN_PROGRESS ? 'bg-purple-600 text-white' :
                    currentStep === step ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {stepStatus[step].status === StepStatus.COMPLETE ? <CheckCircle className="w-5 h-5" /> :
                     stepStatus[step].status === StepStatus.IN_PROGRESS ? <Loader2 className="w-5 h-5 animate-spin" /> : step}
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Icon className={`w-5 h-5 text-${color}-600`} />
                      {title}
                    </CardTitle>
                    <p className="text-sm text-gray-500">{stepStatus[step].hint}</p>
                  </div>
                </div>
                <Badge className={STATUS_CONFIG[stepStatus[step].status].color}>
                  {STATUS_CONFIG[stepStatus[step].status].badgeText}
                </Badge>
              </div>
            </CardHeader>
          </Card>
        </React.Fragment>
      ))}

      {/* P2: Gate Status Summary */}
      {gateStatus.source && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-purple-600" />
                <div>
                  <CardTitle className="text-base">Quality Gates</CardTitle>
                  <p className="text-xs text-gray-500">Automatische Qualitätsprüfung</p>
                </div>
              </div>
              <div className="flex gap-2">
                {gateStatus.source && (
                  <Badge className={
                    gateStatus.source.overall_status === 'PASSED' 
                      ? 'bg-green-100 text-green-800' 
                      : gateStatus.source.overall_status === 'WARNING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }>
                    Source: {gateStatus.source.overall_status}
                  </Badge>
                )}
                {gateStatus.rack && (
                  <Badge className={
                    gateStatus.rack.generation_ready 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }>
                    V4: {gateStatus.rack.generation_ready ? 'Bereit' : 'Nicht bereit'}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Action Buttons */}
      <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">Workflow ausführen</h3>
              <p className="text-sm text-gray-500">
                {currentStep === 1 ? 'Wählen Sie eine Source und klicken Sie "Alle Schritte ausführen"' :
                 currentStep <= 6 ? `Aktueller Schritt: ${currentStep}/6` : 'Workflow abgeschlossen'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={executeCurrentStep}
                disabled={executing || !selectedSourceId || currentStep > 6}
              >
                {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Nächster Schritt
              </Button>
              <Button
                onClick={runAllSteps}
                disabled={executing || !selectedSourceId}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                data-testid="run-all-steps"
              >
                {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Alle Schritte ausführen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Execution Log */}
      {executionLog.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Ausführungsprotokoll</CardTitle>
              {/* Run-Key Policy: Show current run_key (read-only) */}
              {currentRunKey && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Run-Key:</span>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">{currentRunKey}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText(currentRunKey);
                      toast.success('Run-Key kopiert');
                    }}
                  >
                    📋
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto font-mono text-xs">
              {executionLog.map((log, i) => (
                <div key={i} className={`${
                  log.type === 'success' ? 'text-green-400' :
                  log.type === 'error' ? 'text-red-400' :
                  log.type === 'warning' ? 'text-yellow-400' : 'text-gray-300'
                }`}>
                  <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Banner */}
      {completedSteps === 6 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800">Workflow abgeschlossen!</h3>
                <p className="text-sm text-green-600">
                  Alle 6 Schritte erfolgreich durchgeführt. Ihre Exam Items wurden generiert UND validiert.
                </p>
                {stepStatus[6]?.data && (
                  <p className="text-xs text-green-500 mt-1">
                    {stepStatus[6].data.approvedCount || 0} genehmigt • {stepStatus[6].data.validatedCount || 0} validiert
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HappyPathWizard;
